import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { LongTextarea, NoteView } from "@/components/LongText";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Inbox, Send, Plus, Check, X, Clock, CalendarClock, Loader2, CircleDot } from "lucide-react";
import { EntitySearch } from "@/components/EntitySearch";
import { TipoPicker } from "@/components/TipoPicker";
import { TIPO_META } from "@/lib/agendaTipi";

const STATO = {
  in_attesa: { label: "In attesa", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  accettata: { label: "Accettata", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rifiutata: { label: "Rifiutata", cls: "bg-red-50 text-red-700 border-red-200" },
  controproposta: { label: "Controproposta", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  scaduta: { label: "Scaduta", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const fmt = (iso) => { try { return format(new Date(iso), "EEE d MMM HH:mm", { locale: it }); } catch { return "—"; } };
const toISO = (local) => {
  if (!local) return null;
  const d = new Date(local); const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
};

export function RichiestePanel() {
  const [tab, setTab] = useState("ricevute");
  const [data, setData] = useState({ items: [], pending_ricevute: 0 });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [counterFor, setCounterFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/agenda/richieste", { params: { box: tab } }); setData(r.data); }
    catch { setData({ items: [], pending_ricevute: 0 }); } finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, path, body) => {
    setBusyId(id);
    try { await api.post(`/agenda/richieste/${id}/${path}`, body || {}); toast.success("Fatto"); load(); return true; }
    catch (e) { toast.error(apiError(e)); return false; } finally { setBusyId(null); }
  };

  return (
    <div className="fade-up" data-testid="richieste-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1">
          <button data-testid="rich-tab-ricevute" onClick={() => setTab("ricevute")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === "ricevute" ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}>
            <Inbox size={14} /> Ricevute
            {data.pending_ricevute > 0 && <span data-testid="rich-badge" className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{data.pending_ricevute}</span>}
          </button>
          <button data-testid="rich-tab-inviate" onClick={() => setTab("inviate")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === "inviate" ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}>
            <Send size={14} /> Inviate
          </button>
        </div>
        <button data-testid="rich-new-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm glow-pink"><Plus size={16} /> Nuova richiesta</button>
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#FC82ED]" /></div>}
      {!loading && data.items.length === 0 && <p className="text-center text-sm text-[var(--muted)] py-10">Nessuna richiesta.</p>}
      <div className="space-y-2.5">
        {data.items.map((r) => {
          const s = STATO[r.stato] || STATO.in_attesa;
          const when = r.stato === "controproposta" && r.proposta_data ? r.proposta_data : r.data;
          const isRecv = tab === "ricevute";
          return (
            <div key={r.id} data-testid={`rich-${r.id}`} className="card-immo p-3.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{r.motivo || "Appuntamento"}</div>
                  {r.tipo && TIPO_META[r.tipo] ? (
                    <span data-testid={`rich-tipo-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold mt-0.5" style={{ color: TIPO_META[r.tipo].color }}><span className="w-2 h-2 rounded-full" style={{ background: TIPO_META[r.tipo].color }} />{TIPO_META[r.tipo].label}</span>
                  ) : (
                    <span data-testid={`rich-tipo-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 mt-0.5"><span className="w-2 h-2 rounded-full bg-slate-400" />Da classificare</span>
                  )}
                  <div className="text-xs text-[var(--muted)] mt-0.5 flex items-center gap-1.5">
                    <CalendarClock size={12} /> {fmt(when)}{r.durata_min ? ` · ${r.durata_min} min` : ""}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{isRecv ? `Da ${r.from_name}` : `A ${r.to_name}`}{r.immobile_titolo ? ` · ${r.immobile_titolo}` : ""}{r.cliente_nome ? ` · ${r.cliente_nome}` : ""}</div>
                  {r.rifiuto_motivo && <div className="text-xs text-red-600 mt-1">Motivo: {r.rifiuto_motivo}</div>}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${s.cls}`}>{s.label}</span>
              </div>
              {/* actions */}
              {isRecv && r.stato === "in_attesa" && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <button disabled={busyId === r.id} onClick={() => act(r.id, "accetta")} data-testid={`rich-accept-${r.id}`} className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-emerald-500 text-white disabled:opacity-40"><Check size={14} /> Accetta</button>
                  <button disabled={busyId === r.id} onClick={() => setRejectFor(r)} data-testid={`rich-reject-${r.id}`} className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-red-500 text-white disabled:opacity-40"><X size={14} /> Rifiuta</button>
                  <button disabled={busyId === r.id} onClick={() => setCounterFor(r)} data-testid={`rich-counter-${r.id}`} className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)]"><Clock size={14} /> Altro orario</button>
                </div>
              )}
              {!isRecv && r.stato === "controproposta" && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button disabled={busyId === r.id} onClick={() => act(r.id, "accetta")} data-testid={`rich-accept-cp-${r.id}`} className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-emerald-500 text-white disabled:opacity-40"><Check size={14} /> Accetta orario</button>
                  <button disabled={busyId === r.id} onClick={() => setRejectFor(r)} data-testid={`rich-reject-cp-${r.id}`} className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-red-500 text-white disabled:opacity-40"><X size={14} /> Rifiuta</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {counterFor && <ContropropostaModal r={counterFor} busy={busyId === counterFor.id}
        onClose={() => setCounterFor(null)}
        onConfirm={(body) => act(counterFor.id, "controproponi", body)} />}
      {rejectFor && <RifiutoModal r={rejectFor} busy={busyId === rejectFor.id}
        onClose={() => setRejectFor(null)}
        onConfirm={(body) => act(rejectFor.id, "rifiuta", body)} />}

      {showNew && <NuovaRichiestaModal onClose={() => setShowNew(false)} onSent={() => { setShowNew(false); setTab("inviate"); load(); }} />}
    </div>
  );
}

function ContropropostaModal({ r, onClose, onConfirm, busy }) {
  const [dt, setDt] = useState("");
  const [free, setFree] = useState(null);
  const [checking, setChecking] = useState(false);
  const durata = r.durata_min || 60;
  const otherId = r.from_user_id;
  const otherName = r.from_name || "Il richiedente";

  useEffect(() => {
    setFree(null);
    if (!otherId || !dt) return;
    const t = setTimeout(async () => {
      setChecking(true);
      try { const res = await api.get("/agenda/richieste/freebusy", { params: { user_id: otherId, start: toISO(dt), durata_min: durata } }); setFree(!res.data.busy); }
      catch { setFree(null); } finally { setChecking(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [dt, otherId, durata]);

  const submit = async () => {
    if (!dt) return toast.error("Seleziona data e ora");
    const ok = await onConfirm({ data: toISO(dt) });
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="rich-counter-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Clock size={18} className="text-[#FC82ED]" /> Proponi un altro orario</h3>
        <p className="text-xs text-[var(--muted)] mb-4">Richiesta di {otherName} · durata {durata} min</p>
        <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Nuova data e ora</label>
        <input data-testid="rich-counter-dt" type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[var(--border)] text-base" />
        {dt && (
          <div data-testid="rich-counter-freebusy" className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border mt-3 ${checking ? "text-slate-500 border-slate-200" : free ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
            {checking ? <Loader2 size={14} className="animate-spin" /> : <CircleDot size={14} />}
            {checking ? "Verifica disponibilità…" : free ? `${otherName} risulta LIBERO in quell'orario` : `${otherName} risulta OCCUPATO in quell'orario`}
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[var(--border)] font-semibold text-sm">Annulla</button>
          <button data-testid="rich-counter-send" onClick={submit} disabled={busy || !dt} className="flex-1 py-2.5 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm disabled:opacity-60">{busy ? "Invio…" : "Invia controproposta"}</button>
        </div>
      </div>
    </div>
  );
}

function RifiutoModal({ r, onClose, onConfirm, busy }) {
  const [motivo, setMotivo] = useState("");
  const submit = async () => { const ok = await onConfirm({ motivo: motivo.trim() }); if (ok) onClose(); };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="rich-reject-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><X size={18} className="text-red-500" /> Rifiuta richiesta</h3>
        <NoteView text={r.motivo || "Appuntamento"} className="text-xs text-[var(--muted)] mb-4" />
        <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Motivo (facoltativo)</label>
        <LongTextarea data-testid="rich-reject-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Es. non disponibile in quella fascia…" className="w-full px-3 py-2.5 rounded-lg border border-[var(--border)] text-sm" />
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[var(--border)] font-semibold text-sm">Annulla</button>
          <button data-testid="rich-reject-send" onClick={submit} disabled={busy} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white font-semibold text-sm disabled:opacity-60">{busy ? "Invio…" : "Conferma rifiuto"}</button>
        </div>
      </div>
    </div>
  );
}

function NuovaRichiestaModal({ onClose, onSent }) {
  const [users, setUsers] = useState([]);
  const [toUser, setToUser] = useState("");
  const [tipo, setTipo] = useState("");
  const [dt, setDt] = useState("");
  const [durata, setDurata] = useState(60);
  const [motivo, setMotivo] = useState("");
  const [imm, setImm] = useState(null);
  const [cli, setCli] = useState(null);
  const [free, setFree] = useState(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/agenda/richieste/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([])); }, []);

  useEffect(() => {
    setFree(null);
    if (!toUser || !dt) return;
    const t = setTimeout(async () => {
      setChecking(true);
      try { const r = await api.get("/agenda/richieste/freebusy", { params: { user_id: toUser, start: toISO(dt), durata_min: durata } }); setFree(!r.data.busy); }
      catch { setFree(null); } finally { setChecking(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [toUser, dt, durata]);

  const [tipoErr, setTipoErr] = useState("");
  const send = async () => {
    if (!tipo) { setTipoErr("Campo obbligatorio"); return toast.error("Seleziona il tipo di appuntamento"); }
    if (!toUser || !dt) return toast.error("Destinatario e orario obbligatori");
    setSending(true);
    try {
      await api.post("/agenda/richieste", {
        to_user_id: toUser, data: toISO(dt), durata_min: parseInt(durata) || 60, motivo, tipo,
        immobile_id: imm?.id, immobile_titolo: imm?.label, cliente_id: cli?.id, cliente_nome: cli?.label,
      });
      toast.success("Richiesta inviata");
      onSent();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="rich-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-lg mb-4">Richiesta di appuntamento</h3>
        <div className="space-y-3">
          <TipoPicker value={tipo} onChange={(v) => { setTipo(v); setTipoErr(""); }} error={tipoErr} includeBlocked={false} testid="rich-tipo-picker" />
          <div>
            <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Destinatario</label>
            <select data-testid="rich-to" value={toUser} onChange={(e) => setToUser(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[var(--border)]">
              <option value="">Seleziona utente…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.team_name ? ` · ${u.team_name}` : ""}{u.role ? ` (${String(u.role).replace("_", " ")})` : ""}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Data e ora</label>
              <input data-testid="rich-dt" type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[var(--border)]" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Durata (min)</label>
              <input data-testid="rich-durata" type="number" min="15" step="15" value={durata} onChange={(e) => setDurata(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[var(--border)]" />
            </div>
          </div>
          {toUser && dt && (
            <div data-testid="rich-freebusy" className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${checking ? "text-slate-500 border-slate-200" : free ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
              {checking ? <Loader2 size={14} className="animate-spin" /> : <CircleDot size={14} />}
              {checking ? "Verifica disponibilità…" : free ? "Il destinatario risulta LIBERO in quell'orario" : "Il destinatario risulta OCCUPATO in quell'orario"}
            </div>
          )}
          <div>
            <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Motivo</label>
            <LongTextarea data-testid="rich-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Es. Sopralluogo, riunione…" className="w-full px-3 py-2 rounded-lg border border-[var(--border)]" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Immobile (facoltativo)</label>
            <EntitySearch testid="rich-imm" kinds={["immobile", "notizia"]} value={imm} onSelect={setImm} placeholder="Cerca immobile…" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">Cliente (facoltativo)</label>
            <EntitySearch testid="rich-cli" kinds={["proprietario", "acquirente"]} value={cli} onSelect={setCli} placeholder="Cerca cliente…" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[var(--border)] font-semibold text-sm">Annulla</button>
          <button data-testid="rich-send" onClick={send} disabled={sending} className="flex-1 py-2.5 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm disabled:opacity-60">{sending ? "Invio…" : "Invia richiesta"}</button>
        </div>
      </div>
    </div>
  );
}
